// helper/breakpoint.helper.js
import CssProperties from './css-properties.helper.js';

export default class Breakpoint {
  /**
   * Returns a media query string based on a data attribute that contains a CSS variable.
   *
   * @param {HTMLElement} element - The element with the data attribute.
   * @param {string} dataAttr - The data attribute name (e.g. 'data-breakpoint-max').
   * @returns {string|null} The constructed media query string or null if attribute is missing.
   */
  static getMediaQueryFromDataAttr(element, dataAttr) {
    const value = element.getAttribute(dataAttr);
    if (!value) return null;

    const px = Number(CssProperties.getCssPixelProperty(value));
    if (dataAttr.includes('max')) {
      return `(max-width: ${px}px)`;
    }
    if (dataAttr.includes('min')) {
      return `(min-width: ${px + 1}px)`;
    }

    return `(max-width: ${px}px)`; // fallback
  }

  /**
   * Returns a media query string based on a CSS custom property (e.g. '--breakpoint-mobile').
   *
   * @param {string} cssVar - The CSS variable name (e.g. '--breakpoint-mobile').
   * @returns {string} The constructed media query string.
   */
  static getMediaQueryFromCssVar(cssVar) {
    const px = Number(CssProperties.getCssPixelProperty(cssVar));
    return `(max-width: ${px}px)`;
  }

  /**
   * Checks if the current viewport matches the media query defined via a data attribute.
   *
   * @param {HTMLElement} element - The element with the data attribute.
   * @param {string} dataAttr - The name of the data attribute.
   * @returns {boolean} True if the media query matches, or no attribute is present.
   */
  static matches(element, dataAttr) {
    if (!element) return true;
    const query = this.getMediaQueryFromDataAttr(element, dataAttr);
    if (!query) return true; // no attr means "always matches"
    return window.matchMedia(query).matches;
  }

  /**
   * Checks if the current viewport is considered "mobile" based on a CSS variable.
   *
   * @param {string} cssVar - The CSS variable name (e.g. '--breakpoint-mobile').
   * @returns {boolean} True if the viewport width is smaller than or equal to the breakpoint.
   */
  static isMobileByCssVar(cssVar) {
    return window.matchMedia(this.getMediaQueryFromCssVar(cssVar)).matches;
  }

  /**
   * Checks if the current viewport is considered "desktop" based on a CSS variable.
   *
   * @param {string} cssVar - The CSS variable name (e.g. '--breakpoint-mobile').
   * @returns {boolean} True if the viewport width is larger than the breakpoint.
   */
  static isDesktopByCssVar(cssVar) {
    return !this.isMobileByCssVar(cssVar);
  }
}
